import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostBinding,
  inject,
  Injector,
  input,
  signal,
  WritableSignal,
} from '@angular/core';
import { ICONS, LOADING_SERVICE_TOKEN } from '@aksia/infrastructure';
import { ButtonComponent } from '../../input/button/button.component';
import { LabelComponent } from '../label/label.component';
import { UiLoadingDirective } from '../../../directives/ui/ui-loading.directive';

interface SheetData {
  name: string;
  rows: string[][];
}

@Component({
  selector: 'excel-viewer',
  imports: [ButtonComponent, LabelComponent, UiLoadingDirective],
  templateUrl: './excel-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: grid;
      grid-template-rows: auto 1fr;
      width: 100%;
      height: 100%;
      overflow: hidden;
    }
    [uiexcelviewercanvas] {
      overflow: auto;
    }
    [uiexcelviewercanvas] table {
      border-collapse: collapse;
      font-size: 0.8125rem;
    }
    [uiexcelviewercanvas] th,
    [uiexcelviewercanvas] td {
      border: 1px solid var(--ag-border-color, #e2e2e2);
      padding: 4px 8px;
      white-space: nowrap;
      vertical-align: top;
    }
    [uiexcelviewercanvas] thead th {
      position: sticky;
      top: 0;
      background: var(--ag-background-color, #fff);
      font-weight: 600;
    }
    :host-context(.dark) [uiexcelviewercanvas] th,
    :host-context(.dark) [uiexcelviewercanvas] td {
      color: #e5e7eb;
      border-color: var(--ag-border-color, #3a3a3a);
    }
    :host-context(.dark) [uiexcelviewercanvas] thead th {
      background: var(--ag-background-color, #1a1a1a);
    }
  `,
})
export class ExcelViewerComponent {
  //#region Injections

  private readonly loadingService = inject(LOADING_SERVICE_TOKEN);
  private readonly injector = inject(Injector);

  //#endregion

  //#region Constants

  ICONS = ICONS;

  //#endregion

  //#region Host Bindings

  @HostBinding('attr.uicontrol')
  get uicontrol() {
    return '';
  }

  //#endregion

  //#region Inputs

  src = input<string | ArrayBuffer | Uint8Array>();

  //#endregion

  //#region State

  protected sheets: WritableSignal<SheetData[]> = signal([]);
  protected currentSheet: WritableSignal<number> = signal(0);
  protected errorMessage: WritableSignal<string | undefined> =
    signal(undefined);

  private currentLoadKey: string | ArrayBuffer | Uint8Array | undefined;

  //#endregion

  //#region Computed

  protected totalSheets = computed(() => this.sheets().length);
  protected activeSheet = computed(
    () => this.sheets()[this.currentSheet()] ?? null,
  );
  protected sheetLabel = computed(() => {
    const total = this.totalSheets();
    if (!total) return '';
    return `${this.activeSheet()?.name ?? ''} (${this.currentSheet() + 1} / ${total})`;
  });

  //#endregion

  constructor() {
    afterNextRender(() => {
      effect(
        () => {
          const src = this.src();
          if (src) {
            this.loadDocument(src);
          }
        },
        { injector: this.injector },
      );
    });
  }

  //#region Methods

  protected prevSheet() {
    if (this.currentSheet() > 0) {
      this.currentSheet.update((s) => s - 1);
    }
  }

  protected nextSheet() {
    if (this.currentSheet() < this.totalSheets() - 1) {
      this.currentSheet.update((s) => s + 1);
    }
  }

  private async loadDocument(src: string | ArrayBuffer | Uint8Array) {
    if (this.currentLoadKey === src) {
      return;
    }
    this.currentLoadKey = src;

    this.loadingService.addLoadRequest('Excel File', { type: 'area' });
    try {
      let buffer: ArrayBuffer;
      if (typeof src === 'string') {
        console.log('[excel-viewer] fetching', src);
        const response = await fetch(src);
        if (!response.ok) {
          throw new Error(`Failed to fetch (${response.status})`);
        }
        buffer = await response.arrayBuffer();
        console.log('[excel-viewer] fetched bytes', buffer.byteLength);
      } else if (src instanceof Uint8Array) {
        buffer = src.buffer.slice(
          src.byteOffset,
          src.byteOffset + src.byteLength,
        ) as ArrayBuffer;
      } else {
        buffer = src;
      }

      console.log('[excel-viewer] parsing workbook');
      const sheets = await this.parseWorkbook(buffer);
      console.log('[excel-viewer] parsed', sheets.length, 'sheets');

      this.sheets.set(sheets);
      this.currentSheet.set(0);
      this.errorMessage.set(undefined);
    } catch (err) {
      console.error('[excel-viewer] load failed', err);
      this.errorMessage.set(
        err instanceof Error ? err.message : 'Failed to load Excel file.',
      );
      this.sheets.set([]);
      this.currentLoadKey = undefined;
    } finally {
      this.loadingService.setLoadResponse('Excel File');
    }
  }

  private async parseWorkbook(buffer: ArrayBuffer): Promise<SheetData[]> {
    const { read, utils } = await import('xlsx');
    const workbook = read(buffer, { type: 'array' });

    return workbook.SheetNames.map((name) => {
      const rows = utils.sheet_to_json<string[]>(workbook.Sheets[name], {
        header: 1,
        raw: false,
        defval: '',
        blankrows: true,
      });
      const colCount = Math.max(0, ...rows.map((row) => row.length));
      return {
        name,
        rows: rows.map((row) =>
          Array.from({ length: colCount }, (_, c) => String(row[c] ?? '')),
        ),
      };
    });
  }

  //#endregion
}
